import { useRef, useState, useEffect } from 'react';
import { transcribeAudio } from '../services/openai.js';

const PAPER = '#F2EBDC';
const GOLD = '#A67C3A';
const INK = '#1B1814';
const RED = '#8B1A1A';

// Diagnostic page for the mobile mic + Whisper roundtrip.
// Visit /voice-test on the device under test, hold the button, watch the log.
export default function VoiceTest() {
  const [status, setStatus] = useState(
    'Hold the button and speak. Release when done.'
  );
  const [error, setError] = useState('');
  const [log, setLog] = useState([]);
  const [result, setResult] = useState('');
  const [isRecording, setIsRecording] = useState(false);

  const chunksRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);

  function addLog(msg) {
    console.log('[VoiceTest]', msg);
    setLog((prev) => [...prev, msg]);
    setStatus(msg);
  }

  function addError(msg) {
    console.error('[VoiceTest] ERROR:', msg);
    setError(msg);
    setLog((prev) => [...prev, 'ERROR: ' + msg]);
  }

  function getSupportedMimeType() {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isIOS) {
      // iOS Safari lies about webm support — isTypeSupported returns true
      // for webm but the resulting container is malformed and Whisper reports
      // 'duration: 0'. Force mp4 — iOS MediaRecorder produces valid mp4/AAC.
      addLog('iOS detected — forcing audio/mp4');
      return 'audio/mp4';
    }

    // Desktop / Android — prefer webm (smaller, broader Whisper support)
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];
    for (const t of types) {
      const supported = MediaRecorder.isTypeSupported(t);
      addLog(
        'Checking: ' + t + ' → ' + (supported ? 'SUPPORTED' : 'not supported')
      );
      if (supported) return t;
    }
    addLog('No preferred MIME supported — using browser default');
    return '';
  }

  async function startRecording() {
    setResult('');
    setError('');
    setLog([]);
    chunksRef.current = [];
    addLog('--- Recording session start ---');

    // Show the full support matrix so we can correlate with the failing format
    const formatsToCheck = [
      'audio/mp4',
      'audio/webm',
      'audio/webm;codecs=opus',
      'audio/ogg',
      'audio/wav',
    ];
    formatsToCheck.forEach((f) => {
      addLog('Format ' + f + ': ' + (MediaRecorder.isTypeSupported(f) ? 'YES' : 'no'));
    });

    addLog('Requesting microphone…');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      addLog(
        'Got stream — tracks: ' +
          stream
            .getTracks()
            .map((t) => `${t.kind}:${t.readyState}`)
            .join(', ')
      );
    } catch (err) {
      addError('getUserMedia: ' + err.name + ' — ' + err.message);
      return;
    }

    const mimeType = getSupportedMimeType();
    addLog('Using mimeType: ' + (mimeType || 'browser default'));

    let mr;
    try {
      mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    } catch (err) {
      addError('new MediaRecorder: ' + err.name + ' — ' + err.message);
      return;
    }
    mediaRecorderRef.current = mr;

    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
        addLog(
          'chunk ' + chunksRef.current.length + ' (' + e.data.size + ' bytes)'
        );
      } else {
        addLog('chunk fired but data.size = 0');
      }
    };

    mr.onstart = () => {
      setIsRecording(true);
      addLog('MediaRecorder.onstart fired (state: ' + mr.state + ')');
    };

    mr.onerror = (e) => {
      addError('MediaRecorder.onerror: ' + (e?.error?.name || 'unknown'));
    };

    try {
      mr.start(250);
      addLog('mr.start(250) called (effective mime: ' + mr.mimeType + ')');
    } catch (err) {
      addError('mr.start: ' + err.name + ' — ' + err.message);
    }
  }

  async function stopRecording() {
    addLog('Stop requested');

    const mr = mediaRecorderRef.current;
    if (!mr) {
      addError('No mediaRecorder found');
      return;
    }
    if (mr.state === 'inactive') {
      addError('MediaRecorder already inactive');
      return;
    }

    addLog('MediaRecorder state before stop: ' + mr.state);
    setIsRecording(false);

    // Wire up onstop BEFORE calling stop() — otherwise it fires before we attach
    mr.onstop = async () => {
      addLog('MediaRecorder.onstop fired');

      // Release the mic stream
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach((t) => t.stop());
        } catch {}
        streamRef.current = null;
      }

      const finalMime = mr.mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: finalMime });
      addLog('Blob: ' + blob.size + ' bytes, type: ' + finalMime);
      mediaRecorderRef.current = null;

      if (blob.size < 500) {
        addError('Blob too small (' + blob.size + ' bytes) — nothing recorded');
        return;
      }

      addLog('Sending to Whisper…');
      try {
        const text = await transcribeAudio(blob);
        addLog('Whisper returned (' + (text?.length || 0) + ' chars)');
        setResult('Heard: "' + (text || 'nothing') + '"');
      } catch (err) {
        addError('Whisper: ' + (err?.message || 'unknown'));
      }
    };

    // Force any buffered data to flush as a final dataavailable event
    try {
      mr.requestData();
      addLog('requestData() called');
    } catch (err) {
      addLog('requestData() not supported / failed: ' + err.message);
    }

    // Small delay so the requested data event lands, then stop
    await new Promise((r) => setTimeout(r, 200));

    try {
      mr.stop();
      addLog('mr.stop() called');
    } catch (err) {
      addError('mr.stop: ' + err.name + ' — ' + err.message);
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== 'inactive') {
        try {
          mr.stop();
        } catch {}
      }
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach((t) => t.stop());
        } catch {}
        streamRef.current = null;
      }
    };
  }, []);

  return (
    <div
      style={{
        padding: 20,
        background: PAPER,
        minHeight: '100vh',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <h2 style={{ textAlign: 'center', color: INK, margin: '8px 0' }}>
        Voice Test Page
      </h2>

      {/* Current status (latest line) */}
      <div
        style={{
          textAlign: 'center',
          fontSize: 14,
          color: INK,
          margin: '10px 0',
          padding: 10,
          background: 'white',
          borderRadius: 8,
          border: '1px solid #E8DFC8',
          minHeight: 40,
        }}
      >
        {status}
      </div>

      {/* Persistent error banner */}
      {error && (
        <div
          style={{
            background: '#FEE',
            border: '1px solid #C33',
            color: '#900',
            padding: '10px 12px',
            borderRadius: 8,
            margin: '10px 0',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      )}

      {/* Hold button */}
      <button
        type="button"
        onTouchStart={(e) => {
          e.preventDefault();
          startRecording();
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          stopRecording();
        }}
        onTouchCancel={(e) => {
          e.preventDefault();
          stopRecording();
        }}
        onMouseDown={startRecording}
        onMouseUp={stopRecording}
        onMouseLeave={() => {
          if (mediaRecorderRef.current && isRecording) stopRecording();
        }}
        style={{
          display: 'block',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: isRecording ? RED : GOLD,
          color: 'white',
          border: 'none',
          fontSize: 18,
          fontWeight: 'bold',
          margin: '20px auto',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          transform: isRecording ? 'scale(1.08)' : 'scale(1)',
          transition: 'transform 0.15s ease, background 0.15s ease',
          boxShadow: isRecording
            ? '0 0 0 12px rgba(139,26,26,0.18)'
            : '0 6px 20px -6px rgba(166,124,58,0.55)',
        }}
      >
        {isRecording ? 'Release' : 'Hold'}
      </button>

      {/* Whisper result */}
      {result && (
        <div
          style={{
            textAlign: 'center',
            fontSize: 16,
            color: INK,
            margin: '10px 0',
            fontStyle: 'italic',
            padding: 16,
            background: 'white',
            borderRadius: 8,
            border: '1px solid #E8DFC8',
          }}
        >
          {result}
        </div>
      )}

      {/* Full scrolling log — every step stays visible */}
      <div
        style={{
          background: 'white',
          borderRadius: 8,
          padding: 12,
          margin: '10px 0',
          maxHeight: 240,
          overflowY: 'auto',
          fontSize: 12,
          fontFamily: 'monospace',
          border: '1px solid #E8DFC8',
        }}
      >
        {log.length === 0 ? (
          <div style={{ color: '#999', fontStyle: 'italic' }}>
            (log will populate when you start recording)
          </div>
        ) : (
          log.map((line, i) => (
            <div
              key={i}
              style={{
                color:
                  line.includes('ERROR') || line.includes('error')
                    ? '#900'
                    : '#333',
                padding: '2px 0',
                borderBottom: '1px solid #eee',
              }}
            >
              {line}
            </div>
          ))
        )}
      </div>

      {/* User agent for cross-referencing */}
      <p
        style={{
          fontSize: 10,
          color: '#999',
          textAlign: 'center',
          padding: 10,
          wordBreak: 'break-all',
        }}
      >
        {typeof navigator !== 'undefined' ? navigator.userAgent : ''}
      </p>
    </div>
  );
}
